import React from 'react';
import { toast } from 'react-toastify';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ArcIconButton, ArcMenuItem, ArcOutlinedSelect, ArcSkeleton, ArcStack, ArcTable, ArcTableBody, ArcTableCell, ArcTableHead, ArcTableRow } from '@src/core/ui';
import { useDeleteUserMutation, useGetUserRolesQuery, useGetUsersQuery, useSetUserRoleMutation } from '@src/features/settings/redux';
import { CreateUserForm, ResetPasswordForm } from '@src/features/settings/pages/settings-page/components';
import { useGetUserDataQuery, UserEntity, UserRoleType } from '@src/core/features';
import { useConfirm } from '@src/core/controllers';

const UserPanel: React.FC = () => {
  const { data = {} as UserEntity } = useGetUserDataQuery();
  const { data: users, isLoading: isUsersLoading } = useGetUsersQuery();
  const { data: roles = [], isLoading: isRolesLoading } = useGetUserRolesQuery();
  const { confirm } = useConfirm();

  const [setUserRole, { isLoading: isSubmitting }] = useSetUserRoleMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const isOwner = roles.includes(UserRoleType.OWNER) && (String(data.role) === UserRoleType.OWNER || data.role === UserRoleType.OWNER as any);
  const isSuperAdmin = roles.includes(UserRoleType.SUPERADMIN) && (String(data.role) === UserRoleType.SUPERADMIN || data.role === UserRoleType.SUPERADMIN as any);

  const onSetUserRole = (id: number, role: string) => {
    setUserRole({ id, role }).unwrap().then(() => {
      toast.success('SET USER ROLE SUCCESS TEXT');
    }).catch(() => {
      toast.error('Failed to set user role');
    });
  };

  const onDeleteUser = async (userId: number) => {
    if (userId === data.id) {
      toast.error('Cannot delete yourself');
      return;
    }

    const userToDelete = users?.data?.find(u => u.id === userId);
    if (userToDelete?.role === 'OWNER') {
      toast.error('Cannot delete OWNER');
      return;
    }

    const confirmed = await confirm(
      `This action cannot be undone. The user’s account and all associated data will be permanently removed.`,
      'Delete User?'
    );

    if (!confirmed) {
      return;
    }

    deleteUser(userId).unwrap()
      .then(() => {
        toast.success('User deleted successfully');
      })
      .catch((error: any) => {
        const errorMessage = error?.data?.message || error?.message || 'Failed to delete user';
        toast.error(errorMessage);
      });
  };

  if (isUsersLoading || isRolesLoading) {
    return (
      <ArcStack spacing={1}>
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
      </ArcStack>
    );
  }


  return (
    <ArcStack p={2}>
      <CreateUserForm />
      <ArcTable>
        <ArcTableHead>
          <ArcTableRow>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> FULL NAME </ArcTableCell>
            <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> EMAIL </ArcTableCell>
            {data.roleStatus.isAdmin && <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }}> ROLE </ArcTableCell>}
            {data.roleStatus.isAdmin && <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }} />}
            {(isOwner || isSuperAdmin) && <ArcTableCell sx={{ fontWeight: 600, fontSize: 12, border: 0 }} />}
          </ArcTableRow>
        </ArcTableHead>

        <ArcTableBody>
          {users?.data?.map((user) => { 
            const isDisabled = isSubmitting || user.id === data.id || user.role === UserRoleType.SUPERADMIN || user.role === UserRoleType.OWNER;
            
            return (
              <ArcTableRow key={user.id}>
                <ArcTableCell sx={{ border: 0 }}> {user.firstname} {user.lastname} </ArcTableCell>
                <ArcTableCell sx={{ border: 0 }}> {user.email} </ArcTableCell>
                { <ArcTableCell sx={{ border: 0, width: 250 }}> 
                  <ArcOutlinedSelect 
                    sx={{ 
                      width: 250,
                      '& .MuiSelect-select.Mui-disabled': {
                        color: (theme) => theme.palette.text.secondary,
                        WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                        opacity: 1,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme) => 
                          (isDisabled) 
                            ? theme.palette.action.disabled 
                            : 'white',
                      },
                    }}
                    value={user.role} 
                    disabled={isDisabled} 
                    onChange={(e) => onSetUserRole(+user.id, e.target.value as string)}>
                    {roles.map((role) => (
                      <ArcMenuItem key={role} value={role}>
                        {role}
                      </ArcMenuItem>
                    ))}
                  </ArcOutlinedSelect>
                </ArcTableCell>}
                <ArcTableCell sx={{ border: 0, width: 100 }}> 
                  <ArcStack direction='row' spacing={1}>
                    <ResetPasswordForm user={user} /> 
                    <ArcIconButton 
                      onClick={() => onDeleteUser(user.id)}
                      disabled={isDeleting}
                      sx={{ color: 'error.main' }}
                    >
                      {(user.id !== data.id && !isDisabled) && <DeleteOutlineIcon />}
                    </ArcIconButton>
                  </ArcStack>
                </ArcTableCell>
 
              </ArcTableRow>
            );})}
        </ArcTableBody>
      </ArcTable>
    </ArcStack>
  );
};

export default UserPanel;